import React, { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
    mode: ThemeMode;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeMode must be used within ThemeProvider');
    }
    return context;
};

interface ThemeProviderProps {
    children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const [mode, setMode] = useState<ThemeMode>(() => {
        // Load theme preference from localStorage
        const savedMode = localStorage.getItem('themeMode') as ThemeMode;
        return savedMode || 'light';
    });

    const toggleTheme = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            localStorage.setItem('themeMode', newMode);
            return newMode;
        });
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: mode === 'light' ? '#2185d0' : '#64b5f6',
                        light: '#90caf9',
                        dark: '#1678c2',
                        contrastText: '#ffffff',
                    },
                    secondary: {
                        main: mode === 'light' ? '#6435c9' : '#b39ddb',
                        light: '#d1c4e9',
                        dark: '#5829bb',
                        contrastText: '#ffffff',
                    },
                    error: {
                        main: mode === 'light' ? '#db2828' : '#ef5350',
                        light: '#ff695e',
                        dark: '#d01919',
                        contrastText: '#ffffff',
                    },
                    warning: {
                        main: mode === 'light' ? '#f2711c' : '#ff9800',
                        light: '#ffb74d',
                        dark: '#cf590c',
                        contrastText: '#ffffff',
                    },
                    success: {
                        main: mode === 'light' ? '#21ba45' : '#66bb6a',
                        light: '#81c784',
                        dark: '#198f35',
                        contrastText: '#ffffff',
                    },
                    background: {
                        default: mode === 'light' ? '#f5f5f5' : '#2c2c2c',
                        paper: mode === 'light' ? '#ffffff' : '#3a3a3a',
                    },
                    text: {
                        primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.92)',
                        secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
                    },
                    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                },
                typography: {
                    fontFamily: '"Lato", "Helvetica Neue", Arial, Helvetica, sans-serif',
                    fontSize: 14,
                    fontWeightLight: 300,
                    fontWeightRegular: 400,
                    fontWeightMedium: 500,
                    fontWeightBold: 700,
                    button: {
                        textTransform: 'none',
                        fontWeight: 700,
                    },
                },
                shape: {
                    borderRadius: 4,
                },
                components: {
                    MuiAppBar: {
                        styleOverrides: {
                            root: {
                                boxShadow: mode === 'light'
                                    ? '0 2px 4px rgba(0,0,0,0.1)'
                                    : '0 2px 4px rgba(0,0,0,0.3)',
                            },
                        },
                    },
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                borderRadius: '0.28571429rem',
                                padding: '0.78571429em 1.5em',
                                fontWeight: 700,
                            },
                        },
                    },
                    MuiPaper: {
                        styleOverrides: {
                            root: {
                                borderRadius: '0.28571429rem',
                            },
                        },
                    },
                },
            }),
        [mode]
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};

